<template>
  <m-field
    :status="computedErrors.length > 0 ? 'danger' : 'default'"
    :data-testid="testId"
    class="field-wrapper"
    :class="{ 'flex-1': !width, 'military-time': isMilitaryTimeEnabled }">
    <m-field-label v-if="label" slot="label" :data-testid="`${testId}-label`">
      {{ label }}

      <help-tooltip v-if="hint" :text="hint" />
    </m-field-label>
    <div :style="computedWidth" align="center" gap="spacing-2" class="timestamp-group">
      <single-date-picker
        class="timestamp-input"
        @focus="handleFocus"
        @blur="handleBlur"
        v-bind="{ ...removeEventsFromAttrs($attrs) }"
        :name="fieldName"
        :timezone="timezone"
        :placeholder="placeholder"
        :model-value="selectedDate"
        @update:model-value="updateDate"
        :test-id="`${testId}-date-picker-field`" />
      <text-field
        class="p-b-1 timestamp-input"
        :key="renderKey"
        v-bind="{ ...removeEventsFromAttrs($attrs) }"
        @focus="handleFocus"
        @focusout="handleTimeInputBlur"
        ref="time-input"
        :field-name="fieldName"
        :placeholder="placeholder"
        v-model="time"
        @keydown="preventBadKeys"
        :external-error="timeErrors.length > 0 ? timeErrors[0] : null"
        :shouldDisplayMessages="false"
        :test-id="`${testId}-text-field`"></text-field>
      <button-toggle-group
        class="ampm-buttons"
        type="single-strict"
        v-if="!isMilitaryTimeEnabled"
        v-model="ampm"
        :buttons="[
          { value: 'AM', label: 'AM' },
          { value: 'PM', label: 'PM' }
        ]"></button-toggle-group>
      <slot name="trailing"></slot>
    </div>
    <m-field-hint :data-testid="`${testId}-errors`">
      {{ computedErrors.join(', ') }}
    </m-field-hint>
  </m-field>
</template>

<script>
import fieldMixin from '@/components/mixins/fieldMixin';
import validatable from '@/components/mixins/validatable';
import testable from '@/components/mixins/testable';
import {
  LuxonDateTimeFormats,
  formatDateTimeWithMilitarySupport,
  formatClockTime,
  formatParsedClockTime,
  parseClockTimeInput,
  CharacterKeys
} from '@nova/core';
import { SingleDatePicker, TextField, ButtonToggleGroup, HelpTooltip } from '@/components';
import { DateTime } from 'luxon';
import { isNil } from 'lodash';
import renderMixin from '@satellite/components/mixins/renderMixin';
import { removeEventsFromAttrs } from '@satellite/plugins/util';

/**
 * Timestamp entry field. Clock mode follows company timeDisplayFormat via isMilitaryTimeEnabled prop
 * (true = twentyFourHour).
 * @displayName Timestamp Field
 */
export default {
  // TODO: Figure out why text field widths are causing overflow
  name: 'TimeStamp',
  mixins: [fieldMixin, renderMixin, validatable, testable],
  components: { SingleDatePicker, TextField, ButtonToggleGroup, HelpTooltip },
  props: {
    /**
     * @model
     */
    modelValue: {
      type: String,
      required: false
    },
    /**
     * Field wrapper width
     */
    width: {
      type: String,
      required: false,
      default: '400px'
    },
    /**
     * Military time mode
     */
    isMilitaryTimeEnabled: {
      type: Boolean,
      required: false,
      default: false
    },
    /**
     * Timezone to be used
     */
    timezone: {
      type: String,
      required: false,
      default: 'UTC'
    }
  },
  computed: {
    computedErrors() {
      return [...this.errorBucket, ...this.timeErrors];
    }
  },
  data() {
    return {
      selectedDate: null,
      time: null,
      ampm: 'AM',
      timeErrors: [],
      timeRules: null
    };
  },
  methods: {
    removeEventsFromAttrs,
    formatTime(time) {
      if (this.timeErrors.length > 0) {
        return;
      }
      return formatClockTime(time, this.isMilitaryTimeEnabled);
    },
    /**
     * Normalizes the clock text the same way blur does. Returns the padded HH:mm string,
     * or null when the field is empty / out of range (and then sets timeErrors).
     */
    normalizeTimeInput() {
      if (!this.time) {
        this.timeErrors = [];
        return null;
      }

      const parsed = parseClockTimeInput(this.time, this.isMilitaryTimeEnabled);
      if (parsed === null) {
        this.timeErrors = this.util.validateValuesAgainstRules(this.time, this.timeRules) ?? [];
        return null;
      }

      this.timeErrors = [];
      this.time = formatParsedClockTime(parsed);
      return this.time;
    },
    buildValue() {
      const formattedTime = this.formatTime(this.time);
      if (isNil(formattedTime)) {
        return null;
      }
      const date = DateTime.fromISO(this.selectedDate, { zone: this.timezone }).toFormat(
        LuxonDateTimeFormats.DateDashed
      );
      const dateTime = this.isMilitaryTimeEnabled
        ? DateTime.fromFormat(`${date} ${formattedTime}`, 'yyyy-MM-dd H:mm', {
            zone: this.timezone
          })
        : DateTime.fromFormat(`${date} ${formattedTime} ${this.ampm}`, 'yyyy-MM-dd h:mm a', {
            zone: this.timezone
          });

      return dateTime.toUTC().toISO();
    },
    updateVal() {
      this.$nextTick(() => {
        if (!this.normalizeTimeInput()) {
          return;
        }
        this.rerender();

        if (this.selectedDate) {
          const val = this.buildValue();
          if (!isNil(val)) {
            this.internalValue = val;
          }
        } else if (this.modelValue) {
          this.setTimeFromVal();
          this.rerender();
        }
      });
    },
    setTimeFromVal() {
      // 12h mode shows hh:mm in the text field with separate AM/PM toggles (not company `h:mm a`).
      this.time = this.modelValue
        ? formatDateTimeWithMilitarySupport(
            this.modelValue,
            this.timezone,
            LuxonDateTimeFormats.Extended12HrTimePadding,
            this.isMilitaryTimeEnabled,
            LuxonDateTimeFormats.Extended24HrTime
          )
        : null;
    },
    setDateFromVal() {
      this.selectedDate = this.modelValue ?? null;
    },
    setAmPmFromVal() {
      if (!this.isMilitaryTimeEnabled) {
        this.ampm = this.modelValue
          ? DateTime.fromISO(this.modelValue, { zone: this.timezone }).toFormat('a')
          : 'AM';
      }
    },
    setTimeInputRules() {
      this.timeRules = this.isMilitaryTimeEnabled
        ? this.$validator.rules.time24
        : this.$validator.rules.time;
    },
    handleTimeInputBlur() {
      this.handleBlur();
      this.updateVal();
    },
    preventBadKeys(e) {
      const key = e.key;
      const allowedKeys = new Set([
        CharacterKeys.COLON,
        CharacterKeys.LEFT_ARROW,
        CharacterKeys.RIGHT_ARROW,
        CharacterKeys.BACKSPACE,
        CharacterKeys.DELETE
      ]);

      const isNumber = !isNaN(Number(key));
      const isSpacebar = key === CharacterKeys.SPACE;

      if (!allowedKeys.has(key) && (isSpacebar || !isNumber)) {
        e.preventDefault();
      } else {
        return true;
      }
    },
    updateDate(date) {
      this.selectedDate = date;
      this.updateVal();
    }
  },
  mounted() {
    this.setTimeInputRules();
    this.setDateFromVal();
    this.setTimeFromVal();
    this.setAmPmFromVal();
  },
  watch: {
    modelValue() {
      this.setDateFromVal();
      this.setTimeFromVal();
      this.setAmPmFromVal();
    },
    time() {
      // Clear stale validation while the user edits; the value is validated and
      // normalized on blur (see handleTimeInputBlur).
      if (this.timeErrors.length) {
        this.timeErrors = [];
      }
    }
  }
};
</script>

<style lang="scss" scoped>
.timestamp-input {
  overflow: hidden;
}

:host {
  --m-grid-min-column-width: 30px !important;
}

.timestamp-group {
  display: grid;
  grid-template-columns: 1fr 1fr 92px;
  grid-gap: $m-spacing-2;

  @media (max-width: $tabletBreakpoint) {
    width: auto !important;
  }
}
</style>
